const oneDayMs = 1_000 * 60 * 60 * 24;

type StoredValue<T> = {
    value: T;
    expiresAt: number;
};

export function createSelfCleaningClientStorage<K extends string | number, V>(
    storageKeyName: string,
    expiryMs: number = oneDayMs, // 1 day by default
) {
    const currentStorage: Record<K, StoredValue<V>> = JSON.parse(localStorage.getItem(storageKeyName) || '{}') as Record<K, StoredValue<V>>;

    function saveCurrentStorage(): void {
        localStorage.setItem(storageKeyName, JSON.stringify(currentStorage));
    }
    function clearExpiredValues() {
        const now = Date.now();
        for (const key in currentStorage) {
            const { expiresAt } = currentStorage[key];
            if (now > expiresAt) {
                delete currentStorage[key];
            }
        }
    }

    return {
        set(key: K, value: V, shouldSaveAfterChange = false) {
            currentStorage[key] = {
                value,
                expiresAt: Date.now() + expiryMs,
            };
            if (shouldSaveAfterChange) {
                saveCurrentStorage();
            }
        },
        get(key: K, shouldClearExpiredBeforeGetting = false): V | undefined {
            if (shouldClearExpiredBeforeGetting) {
                clearExpiredValues();
                saveCurrentStorage();
            }
            return currentStorage?.[key]?.value;
        },
        remove(key: K, shouldSaveAfterChange = false) {
            delete currentStorage[key];
            if (shouldSaveAfterChange) {
                saveCurrentStorage();
            }
        },
        setMany(records: Record<K, V>) {
            clearExpiredValues();
            for (const key in records) {
                if (!this.get(key)) {
                    this.set(key, records[key], false);
                }
            }
            saveCurrentStorage();
        },
    };
}

export interface SelfCleaningClientStorage<K extends string | number, V> {
  setMany(values: Record<K, V>): void;
  get(key:K): V | undefined;
}
